import type { AppClient } from './queries';
import type { Database } from './supabase/database.types';

/**
 * Turning a media key into something a browser can load — or, much more often,
 * into nothing at all.
 *
 * Three rules, and all three are decisions rather than implementation details:
 *
 *   1. **The source is read at runtime**, from `public.media_source`, never from
 *      a build-time variable. CI builds the frontend once and both environments
 *      reuse the same bytes, so a build-time value would make swapping the media
 *      source a rebuild — the one thing this design forbids (design C4).
 *   2. **Links are minted in one call per page, not one per exercise.** A
 *      session or a page of search results asks for every key it is about to
 *      show, once.
 *   3. **Links live for an hour**, not the twenty-four hours a coach's own video
 *      uses. A signed link is a bearer token until it expires, and catalogue
 *      animations are browsed in bulk rather than watched once.
 *
 * When the source is missing, disabled, or has no key for an exercise, this
 * returns nothing and the product renders its no-media branch. Those three are
 * deliberately the *same* outcome: the day the borrowed media is deleted must
 * not be the day anybody discovers what the product looks like without it.
 */

export type MediaSourceRow = Database['public']['Tables']['media_source']['Row'];

/** The catalogue's media. The only kind there is today. */
export const EXERCISE_CATALOG_MEDIA = 'exercise_catalog';

/**
 * One hour (design C2). Long enough to read a session, short enough that a
 * link copied out of a network panel is worthless by the time it travels.
 */
export const SIGNED_LINK_SECONDS = 3600;

/** The runtime configuration row, or `null` when there is none. */
export async function getMediaSource(
  client: AppClient,
  id: string = EXERCISE_CATALOG_MEDIA,
): Promise<MediaSourceRow | null> {
  const result = await client.from('media_source').select('*').eq('id', id).maybeSingle();
  if (result.error) throw new Error(result.error.message);
  return result.data;
}

/**
 * The narrowest shape of the storage client this needs.
 *
 * Written out rather than taking a whole `SupabaseClient` so that the test that
 * counts minting calls can pass a fake and the count means something. A real
 * client satisfies it structurally.
 */
export interface SigningClient {
  storage: {
    from(bucket: string): {
      createSignedUrls(
        paths: string[],
        expiresIn: number,
      ): Promise<{
        // `signedUrl` is nullable in the client library: a path the store has
        // no object for comes back in the array with nothing in it rather than
        // being left out, so the caller has to read past it.
        data: { path: string | null; signedUrl: string | null }[] | null;
        error: { message: string } | null;
      }>;
    };
  };
}

/** `path_prefix` + key + `.gif`, which is the whole of the addressing scheme. */
export function mediaPath(source: MediaSourceRow, mediaKey: string): string {
  return `${source.path_prefix}${mediaKey}${source.file_extension}`;
}

/**
 * Signed links for every key a page is about to show, keyed by media key.
 *
 * **One call**, whatever the page holds. Keys are deduplicated first, because a
 * session that prescribes the same movement twice must not pay twice.
 *
 * A key that the store has no object for comes back missing rather than broken:
 * the caller renders the no-media branch for it, exactly as it would for an
 * exercise with no key at all.
 */
export async function resolveMediaUrls(
  client: SigningClient,
  source: MediaSourceRow | null,
  mediaKeys: readonly (string | null)[],
): Promise<Map<string, string>> {
  const resolved = new Map<string, string>();
  if (source?.enabled !== true) return resolved;

  const keys = [...new Set(mediaKeys.filter((key): key is string => key !== null && key !== ''))];
  if (keys.length === 0) return resolved;

  const paths = keys.map((key) => mediaPath(source, key));
  const { data, error } = await client.storage
    .from(source.bucket)
    .createSignedUrls(paths, SIGNED_LINK_SECONDS);

  // A store that is gone — deleted at expiry, or never created — is not an
  // error the client should see. It is the no-media branch.
  if (error || !data) return resolved;

  const keyByPath = new Map(paths.map((path, index) => [path, keys[index] ?? '']));
  for (const row of data) {
    const key = row.path === null ? undefined : keyByPath.get(row.path);
    const url = row.signedUrl ?? '';
    if (key !== undefined && key !== '' && url !== '') resolved.set(key, url);
  }

  return resolved;
}
